import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import styled from 'styled-components';

import { SrOnly } from '@/components/Layout/Parts';
import { Table, TableContent, Td, Th } from '@/components/Table';
import { PinterestBoard } from '@/types/resources';
import { api } from '@/utils/api';

import CopyButton from './CopyButton';

const StyledTable = styled(Table)`
  th,
  td {
    padding-top: 16px;
    padding-bottom: 16px;
    &:nth-child(1) {
      width: 40%;
      padding-left: 30px;
    }
    &:nth-child(2) {
      width: 30%;
    }
    &:nth-child(3) {
      width: 30%;
    }
  }
  td {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
  }
`;

const PageHolder = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Thumbnail = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

interface Props {
  channelId: number;
}

const PinterestBoardsTable: FC<Props> = ({ channelId }) => {
  const {
    data: pinterestBoards,
    isLoading,
    isError,
  } = useQuery(
    ['pinterest_boards', { channelId }],
    () =>
      api.get<never, PinterestBoard[]>('/v1/editorial/queue/pinterest-boards', {
        params: {
          channel_id: channelId,
        },
      }),
    {
      staleTime: 120_000,
    },
  );

  return (
    <StyledTable $fixed>
      <thead>
        <tr>
          <Th>Board Name</Th>
          <Th>ID</Th>
          <Th>
            <SrOnly>Actions</SrOnly>
          </Th>
        </tr>
      </thead>
      <tbody>
        <TableContent
          isLoading={isLoading}
          hasData={!!pinterestBoards?.length}
          isError={isError}
        >
          {pinterestBoards?.map((b) => (
            <tr key={b.id}>
              <Td>
                <PageHolder>
                  {!!b.thumbnail && (
                    <Thumbnail src={b.thumbnail} alt={b.name} />
                  )}
                  {b.name}
                </PageHolder>
              </Td>
              <Td>{b.id}</Td>
              <Td>
                <Actions>
                  <CopyButton idToCopy={parseInt(b.id)} />
                </Actions>
              </Td>
            </tr>
          ))}
        </TableContent>
      </tbody>
    </StyledTable>
  );
};

export default PinterestBoardsTable;
